"use client";

import { type ReactNode, useEffect, useRef, useState } from "react";
import { ArrowRight, UserRound, UsersRound } from "lucide-react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";

import { Button } from "~/components/ui/button";
import { SelectableOption } from "~/components/ui/selectable-option";
import { pushGA4Event } from "~/lib/ga4-browser";
import { capturePostHogEvent } from "~/lib/posthog/client";
import { type TherapyRelationshipType } from "~/prisma/browser";
import { api } from "~/trpc/react";

import { GuidedQuestionnaire } from "./guided-questionnaire";
import {
  matchTherapistCouplesQuestions,
  matchTherapistQuestions,
  type TherapistMatchingAnswer,
} from "./questions";

type QuestionnaireAnswer = {
  question: string;
  answer: TherapistMatchingAnswer;
};

export default function TherapistMatchingQuestionnaire({
  startHeader,
  therapistProof,
}: {
  startHeader?: ReactNode;
  therapistProof?: ReactNode;
}) {
  const router = useRouter();
  const { data: session } = useSession();
  const [therapyType, setTherapyType] =
    useState<TherapyRelationshipType | null>(null);
  const [selectedTherapyType, setSelectedTherapyType] =
    useState<TherapyRelationshipType | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const matchWithTherapist = api.ai.matchWithTherapist.useMutation({
    onSuccess: (data) => {
      pushGA4Event("generate_lead", {
        currency: "EUR",
        value: 0,
        content_id: "therapist_matching",
      });
      capturePostHogEvent("questionnaire_completed", {
        therapy_type: therapyType,
      });
      router.push(
        session?.user
          ? `/app/testovi/therapist-matching/${data.id}`
          : `/upitnik/${data.id}`,
      );
    },
    onError: () => {
      capturePostHogEvent("questionnaire_failed", {
        therapy_type: therapyType,
      });
      setSubmissionError(
        "Preporuke trenutno ne možemo da pripremimo. Tvoji odgovori su sačuvani ovde, pa pokušaj ponovo.",
      );
    },
  });

  const handleStart = () => {
    if (!selectedTherapyType) return;

    setSubmissionError(null);
    setTherapyType(selectedTherapyType);
    capturePostHogEvent("questionnaire_started", {
      therapy_type: selectedTherapyType,
    });
  };

  const handleSubmit = ({
    answers,
    email,
    newsletterOptIn,
  }: {
    answers: QuestionnaireAnswer[];
    email?: string;
    newsletterOptIn: boolean;
  }) => {
    setSubmissionError(null);
    matchWithTherapist.mutate({
      questionnaireContent: JSON.stringify({
        ...(email ? { email } : {}),
        answers,
      }),
      therapyType: therapyType ?? "INDIVIDUAL",
      newsletterOptIn,
    });
  };

  if (therapyType) {
    return (
      <GuidedQuestionnaire
        questions={
          therapyType === "COUPLES"
            ? matchTherapistCouplesQuestions
            : matchTherapistQuestions
        }
        therapyType={therapyType}
        initialEmail={session?.user?.email}
        errorMessage={submissionError}
        isSubmitting={matchWithTherapist.isPending}
        onBackToStart={() => setTherapyType(null)}
        onSubmit={handleSubmit}
      />
    );
  }

  return (
    <MatchingStart
      onSelectTherapyType={setSelectedTherapyType}
      onStart={handleStart}
      selectedTherapyType={selectedTherapyType}
      startHeader={startHeader}
      therapistProof={therapistProof}
    />
  );
}

function MatchingStart({
  onSelectTherapyType,
  onStart,
  selectedTherapyType,
  startHeader,
  therapistProof,
}: {
  onSelectTherapyType: (type: TherapyRelationshipType) => void;
  onStart: () => void;
  selectedTherapyType: TherapyRelationshipType | null;
  startHeader?: ReactNode;
  therapistProof?: ReactNode;
}) {
  // Clock reads must not run during prerender (Next.js treats Date.now() in
  // client render as an unstable value); the ref is seeded on mount instead.
  const entryStartedAtRef = useRef<number | null>(null);
  const entryViewedRef = useRef(false);
  const exitCapturedRef = useRef(false);
  const startClickedRef = useRef(false);
  const selectedTherapyTypeRef = useRef<TherapyRelationshipType | null>(null);
  const maxScrollDepthRef = useRef<"top" | "partial" | "mostly" | "end">("top");
  const pendingExitTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (pendingExitTimerRef.current !== null) {
      window.clearTimeout(pendingExitTimerRef.current);
      pendingExitTimerRef.current = null;
    }

    entryStartedAtRef.current ??= performance.now();

    if (!entryViewedRef.current) {
      entryViewedRef.current = true;
      capturePostHogEvent("questionnaire_entry_viewed");
    }

    const updateMaxScrollDepth = () => {
      const scrollableHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPosition = Math.max(
        window.scrollY,
        document.documentElement.scrollTop,
      );
      const scrollPercent =
        scrollableHeight > 0 ? (scrollPosition / scrollableHeight) * 100 : 0;

      if (scrollPercent >= 90) {
        maxScrollDepthRef.current = "end";
      } else if (scrollPercent >= 50 && maxScrollDepthRef.current !== "end") {
        maxScrollDepthRef.current = "mostly";
      } else if (scrollPercent > 0 && maxScrollDepthRef.current === "top") {
        maxScrollDepthRef.current = "partial";
      }
    };

    const captureAbandonment = () => {
      if (startClickedRef.current || exitCapturedRef.current) return;

      exitCapturedRef.current = true;
      capturePostHogEvent("questionnaire_entry_abandoned", {
        duration_seconds: Math.round(
          (performance.now() - (entryStartedAtRef.current ?? 0)) / 1000,
        ),
        max_scroll_depth: maxScrollDepthRef.current,
        selected_therapy_type: selectedTherapyTypeRef.current,
      });
    };

    window.addEventListener("scroll", updateMaxScrollDepth, { passive: true });
    window.addEventListener("pagehide", captureAbandonment);

    return () => {
      window.removeEventListener("scroll", updateMaxScrollDepth);
      window.removeEventListener("pagehide", captureAbandonment);
      pendingExitTimerRef.current = window.setTimeout(captureAbandonment, 0);
    };
  }, []);

  const handleSelectTherapyType = (type: TherapyRelationshipType) => {
    selectedTherapyTypeRef.current = type;
    onSelectTherapyType(type);
    capturePostHogEvent("questionnaire_therapy_type_selected", {
      therapy_type: type,
    });
  };

  const handleStart = () => {
    if (!selectedTherapyType) return;

    startClickedRef.current = true;
    capturePostHogEvent("questionnaire_start_clicked", {
      therapy_type: selectedTherapyType,
      duration_seconds: Math.round(
        (performance.now() - (entryStartedAtRef.current ?? 0)) / 1000,
      ),
      max_scroll_depth: maxScrollDepthRef.current,
    });
    onStart();
  };

  return (
    <>
      {startHeader}
      <section className="min-h-[calc(100dvh-4rem)] bg-background py-8 sm:py-12 lg:min-h-0 lg:py-14">
        <div className="max-w-8xl container mx-auto px-4 md:px-8">
          <div className="max-w-6xl lg:grid lg:grid-cols-[minmax(0,48rem)_minmax(16rem,1fr)] lg:items-center lg:gap-16">
            <div className="max-w-3xl">
              <fieldset>
                <legend className="text-sm font-semibold text-foreground">
                  Za koga tražiš terapeuta?
                </legend>
                <div className="mt-3 grid gap-2">
                  <SelectableOption
                    className="min-h-24"
                    control={
                      <input
                        checked={selectedTherapyType === "INDIVIDUAL"}
                        className="h-5 w-5 accent-brand"
                        name="therapy-type"
                        onChange={() => handleSelectTherapyType("INDIVIDUAL")}
                        type="radio"
                        value="INDIVIDUAL"
                      />
                    }
                    controlPosition="end"
                    selected={selectedTherapyType === "INDIVIDUAL"}
                  >
                    <span className="flex items-center gap-4">
                      <UserRound
                        aria-hidden="true"
                        className="h-7 w-7 shrink-0 text-brand"
                      />
                      <span className="flex flex-col gap-1">
                        <span className="text-lg font-bold leading-snug">
                          Individualna terapija
                        </span>
                        <span className="text-sm font-normal leading-5 text-muted-foreground">
                          Dolazim sam/a, bez obzira na temu
                        </span>
                      </span>
                    </span>
                  </SelectableOption>
                  <SelectableOption
                    className="min-h-24"
                    control={
                      <input
                        checked={selectedTherapyType === "COUPLES"}
                        className="h-5 w-5 accent-brand"
                        name="therapy-type"
                        onChange={() => handleSelectTherapyType("COUPLES")}
                        type="radio"
                        value="COUPLES"
                      />
                    }
                    controlPosition="end"
                    selected={selectedTherapyType === "COUPLES"}
                  >
                    <span className="flex items-center gap-4">
                      <UsersRound
                        aria-hidden="true"
                        className="h-7 w-7 shrink-0 text-brand"
                      />
                      <span className="flex flex-col gap-1">
                        <span className="text-lg font-bold leading-snug">
                          Partnerska terapija
                        </span>
                        <span className="text-sm font-normal leading-5 text-muted-foreground">
                          Dolazimo zajedno
                        </span>
                      </span>
                    </span>
                  </SelectableOption>
                </div>
              </fieldset>

              <Button
                className="mt-6 min-w-36"
                disabled={!selectedTherapyType}
                onClick={handleStart}
                type="button"
                variant="cta"
              >
                Nastavi na pitanja
                <ArrowRight aria-hidden="true" className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {therapistProof && (
              <aside className="mt-7 max-w-3xl sm:mt-10 lg:mt-0 lg:max-w-sm">
                {therapistProof}
              </aside>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
